import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";
import "./Delegation.scss";
import React, { useEffect, useState } from "react";
import caseStore from "../../stores/CaseStore";
import {
  Case as CaseType,
  CaseStatus,
  ContactChoice,
} from "../../stores/CaseStore/types";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { cityMap, districtCityMap, districtMap } from "../../utils/cityCodes";
import authStore from "../../stores/AuthStore";
import { UserRoles } from "../../stores/AuthStore/types";
import { caseValidations } from "./validations";
import feedbackStore from "../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../stores/FeedbackStore/types";
import offerStore from "../../stores/OfferStore";
import OfferDialog from "../../components/Offer/OfferDialog";
import OfferResultDialog from "../../components/Offer/OfferResultDialog";
import { Offer } from "../../stores/OfferStore/types";
import configStore, { Flags } from "../../stores/ConfigStore";
import { UpdateCaseStatus } from "../CaseDetails/UpdateCaseStatus";
import ReactGA from "react-ga4";

const defaultValues = {
  id: "",
  status: CaseStatus.DEACTIVE,
  type: "",
  subType: "",
  caseProvince: "",
  district: "",
  description: "",
  budget: "500-1000",
  currency: "TL",
  contactChoice: [],
  createDate: 0,
  updateDate: 0,
};

interface DelegationProps {
  caseItem?: CaseType;
  reviewMode?: boolean;
  afterSubmit?: (updatedCase: CaseType, isSuccessful: boolean) => void;
  onCancel?: () => void;
}

const Delegation = observer((props: DelegationProps) => {
  const isAdmin = authStore.userHasRole(UserRoles.ADMIN);
  const { id } = props.caseItem || useParams<{ id: string | undefined }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [caseItem, setCaseItem] = useState<CaseType | undefined>(
    props.caseItem
  );
  const [caseOffers, setCaseOffers] = useState<Offer[]>();
  const [activeStep, setActiveStep] = useState(0);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [hasSubmit, setHasSubmit] = useState(false);
  const [openOfferDialog, setOpenOfferDialog] = useState(false);
  const [openOfferResultDialog, setOpenOfferResultDialog] = useState(false);
  const [kvkkChecked, setKvkkChecked] = useState(false);
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    trigger,
    reset,
  } = useForm<CaseType>({ defaultValues });

  const editMode =
    authStore.user.id === caseItem?.ownerId || id === "new" || props.caseItem;

  if (
    !isAdmin &&
    (!authStore.user.emailValid ||
      !authStore.user.phone ||
      !authStore.user.firstName ||
      !authStore.userHasRole(UserRoles.LAWYER))
  ) {
    feedbackStore.setShowFeedback(true, {
      severity: FeedbackSeverity.WARNING,
      message: "update_contact_info",
    });
    navigate("/profile");
  }

  useEffect(() => {
    if (offerStore.userOffers.length === 0) {
      offerStore.fetchUserOffers();
    }
    if (offerStore.lawyerOffers.length > 0) {
      offerStore.fetchLawyerOffers();
    }
  }, []);

  useEffect(() => {
    if (id && id !== "new") {
      caseStore.fetchCase(id).then((userCase) => {
        setCaseItem(userCase);
        setActiveStep(4);
        setIsPageLoaded(true);
      });
    } else if (id === "new") {
      reset();
      setCaseItem(undefined);
      setActiveStep(0);
      setIsPageLoaded(true);
    } else {
      if (props.reviewMode) {
        setActiveStep(5);
      }

      setIsPageLoaded(true);
    }
  }, [id]);

  useEffect(() => {
    if (caseItem) {
      setValue("id", caseItem.id);
      setValue("caseProvince", caseItem.caseProvince);
      setValue("district", caseItem.district);
      setValue("description", caseItem.description);
      setValue("budget", caseItem.budget);
      setValue("currency", caseItem.currency);
      setValue("contactChoice", caseItem.contactChoice || []);
      setValue("createDate", caseItem.createDate);
      setValue("updateDate", caseItem.updateDate);
      trigger().then();
    } else {
      setValue("budget", configStore.appConfig.offerRanges[0]);
    }
  }, [caseItem]);

  useEffect(() => {
    setCaseOffers(
      offerStore.userOffers.filter(
        (offer) => offer.caseId.toString() === id?.toString()
      )
    );
  }, [offerStore.userOffers]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit = (data: CaseType) => {
    setHasSubmit(true);

    ReactGA.event({
      category: "User Interaction",
      action: "Submit Case Click",
      label: "Lawyer Case Submit",
      value: Number.parseInt(authStore.user.id),
    });

    data = {
      ...data,
      language: configStore.currentLanguage,
      ownerRole: UserRoles.LAWYER,
    };

    (!!id && id !== "new"
      ? caseStore.updateCase(data, id)
      : caseStore.createCase(data)
    )
      .then(() => {
        if (isAdmin || props.reviewMode) {
          if (props.afterSubmit) {
            props.afterSubmit(data, true);
          }
        } else {
          navigate("/cases");
        }

        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.SUCCESS,
          message: isAdmin || props.reviewMode ? "success" : "case-saved",
        });
      })
      .catch(() => {
        setHasSubmit(false);
        feedbackStore.setShowFeedback(true, {
          severity: FeedbackSeverity.ERROR,
          message: "case-save-failed",
        });
      });
  };

  const handleOfferDialogOpen = () => {
    setOpenOfferDialog(true);
  };

  const summary = (
    <>
      <main>
        {authStore.userHasRole(UserRoles.ADMIN) && (
          <>
            <p>
              <b>{t("user-id")}: </b>
            </p>
            <p>
              <span>{caseItem?.ownerId}</span>
            </p>
          </>
        )}
        <p>
          <b>{t("case-code")}: </b>
        </p>
        <p>
          <span>{caseItem?.id}</span>
        </p>
        <p>
          <b>{t("city")}: </b>
        </p>
        <p>
          <span>{cityMap[getValues("caseProvince")]}</span>
        </p>
        <p>
          <b>{t("district")}: </b>
        </p>
        <p>
          <span>{districtMap[getValues("district") || ""]}</span>
        </p>
        <p>
          <b>{t("case-short-desc")}: </b>
        </p>
        <p>
          <span>{getValues("description")}</span>
        </p>
        <p>
          <b>{t("budget")}: </b>
        </p>
        <p>
          <span>{t(`${getValues("budget")}-budget`)}</span>
        </p>
        <p>
          <b>{t("contact-choice")}: </b>
        </p>
        <p>
          <span>
            {t(
              getValues("contactChoice")
                .map((choice) => t(choice))
                .join(",")
            )}
          </span>
        </p>
      </main>
    </>
  );

  const offers = (
    <div className="seeOffers">
      {id !== "new" && (
        <div>
          <Button
            variant={"outlined"}
            color={"info"}
            onClick={() => setOpenOfferResultDialog(true)}
          >
            {t("see-offers", [`${caseOffers?.length || 0}`])}
          </Button>
          {caseItem && caseOffers && (
            <OfferResultDialog
              offers={caseOffers}
              open={openOfferResultDialog}
              setOpen={setOpenOfferResultDialog}
            />
          )}
        </div>
      )}
    </div>
  );

  const header = (
    <header>
      {id !== "new" && `${id}` !== "" && (
        <h3>
          <span>{t("case-code")}: </span>
          <span>{id}</span>
        </h3>
      )}
      {caseItem && <UpdateCaseStatus caseItem={caseItem} />}
      <p>{t("delegationAgreement")}</p>
    </header>
  );

  const form = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb">
        * {t("your-case-limit", [`${configStore.appConfig.caseLimit}`])}
      </div>
      <Stepper
        orientation={"vertical"}
        activeStep={activeStep}
        sx={{ display: props.reviewMode ? "none" : "block" }}
      >
        <Step>
          <StepLabel onClick={() => setActiveStep(0)}>
            {t("case-city-selection")}
          </StepLabel>
          <StepContent className="pt">
            <Controller
              name={"caseProvince"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  <InputLabel id="case-city-label">
                    {t("please-select-city")}
                  </InputLabel>
                  <Select
                    displayEmpty
                    onChange={(e) => {
                      trigger("caseProvince").then();
                      onChange(e);
                    }}
                    value={value}
                    label={t("please-select-city")}
                    labelId="case-city-label"
                  >
                    {Object.keys(cityMap)
                      .filter((code) => code !== "0")
                      .map((cityCode) => (
                        <MenuItem key={cityCode} value={cityCode}>
                          {cityMap[cityCode]}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name={"district"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  <InputLabel id="user-city-label">
                    {t("please-select-district")}
                  </InputLabel>
                  <Select
                    displayEmpty
                    onChange={(e) => {
                      trigger("district").then();
                      onChange(e);
                    }}
                    value={value}
                    label={t("please-select-district")}
                    labelId="user-city-label"
                  >
                    {Object.keys(districtMap)
                      .filter(
                        (district) =>
                          districtCityMap[district] ===
                          getValues("caseProvince")
                      )
                      .sort((a, b) => a.localeCompare(b))
                      .map((cityCode) => (
                        <MenuItem key={cityCode} value={cityCode}>
                          {districtMap[cityCode]}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />
            <footer>
              <Button
                hidden={
                  `${getValues("district")}` === "" ||
                  `${getValues("caseProvince")}` === ""
                }
                onClick={handleNext}
                variant={"contained"}
              >
                {t("next")}
              </Button>
            </footer>
          </StepContent>
        </Step>
        <Step>
          <StepLabel
            onClick={() =>
              `${getValues("caseProvince")}` !== "" &&
              `${getValues("district")}` !== "" &&
              setActiveStep(1)
            }
          >
            {t("case-description")}
          </StepLabel>
          <StepContent>
            <Controller
              name={"description"}
              control={control}
              rules={{ ...caseValidations.description }}
              render={({ field: { onChange, value } }) => (
                <>
                  <InputLabel className="mb mt">
                    {t("please-enter-short-desc-lawyer")}
                  </InputLabel>
                  <TextField
                    error={!!errors.description}
                    placeholder={t("please-write-here")}
                    multiline={true}
                    rows={5}
                    onChange={(e) => {
                      onChange(e);
                      trigger("description").then();
                    }}
                    value={value}
                  />
                  <Stack
                    direction={"row"}
                    justifyContent={"flex-end"}
                    className={"mb"}
                  >
                    {`${value.length}/${caseValidations.description.maxLength}`}
                  </Stack>
                  <div className="error">
                    {errors.description &&
                      t(`short-desc-${errors.description.type}`)}
                  </div>
                </>
              )}
            />
            <footer>
              <Button
                color={"inherit"}
                onClick={handleBack}
                variant={"contained"}
              >
                {t("back")}
              </Button>
              <Button
                disabled={
                  !!errors.description || `${getValues("description")}` === ""
                }
                onClick={handleNext}
                variant={"contained"}
              >
                {t("next")}
              </Button>
            </footer>
          </StepContent>
        </Step>
        <Step>
          <StepLabel
            onClick={() =>
              `${getValues("description")}` !== "" && setActiveStep(2)
            }
          >
            {t("budget")}
          </StepLabel>
          <StepContent className="pt">
            <Controller
              name={"budget"}
              control={control}
              rules={{ ...caseValidations.budget }}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  {authStore.userHasRole(UserRoles.USER) ? (
                    <Select
                      fullWidth
                      className="mb mt"
                      value={value}
                      onChange={(e) => {
                        if (e.target.value) {
                          onChange(e);
                          trigger("budget").then();
                        }
                      }}
                    >
                      {configStore.appConfig?.offerRanges?.map((range) => (
                        <MenuItem key={range} value={range}>
                          {t(`${range}-budget`)}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <TextField
                      error={!!errors.budget}
                      placeholder={t("please-write-here")}
                      multiline={true}
                      rows={1}
                      onChange={(e) => {
                        if (e.target.value) {
                          onChange(e);
                          trigger("budget").then();
                        }
                      }}
                      value={value}
                    />
                  )}
                  <div className="error mt">
                    {errors.budget && t(`budget-${errors.budget.type}`)}
                  </div>
                </FormControl>
              )}
            />
            <footer>
              <Button
                color={"inherit"}
                onClick={handleBack}
                variant={"contained"}
              >
                {t("back")}
              </Button>
              <Button
                disabled={`${getValues("budget")}` === ""}
                onClick={handleNext}
                variant={"contained"}
              >
                {t("next")}
              </Button>
            </footer>
          </StepContent>
        </Step>
        <Step>
          <StepLabel
            onClick={() =>
              `${getValues("description")}` !== "" && setActiveStep(3)
            }
          >
            {t("contact-choice")}
          </StepLabel>
          <StepContent>
            <Controller
              name={"contactChoice"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <FormControl>
                  {value.length < 1 && (
                    <InputLabel id="contact-choice-label">
                      {t("please-select-one-contact-choice")}
                    </InputLabel>
                  )}
                  <Select
                    fullWidth
                    multiple
                    labelId="contact-choice-label"
                    placeholder={"0"}
                    className="mt"
                    value={value}
                    renderValue={(selected) =>
                      selected.map((choice) => t(choice)).join(",")
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        onChange(e);
                        trigger("contactChoice").then();
                      }
                    }}
                  >
                    {Object.values(ContactChoice).map((item) => (
                      <MenuItem key={item} value={item}>
                        <Checkbox checked={value.includes(item)} />
                        <ListItemText primary={t(item)} />
                      </MenuItem>
                    ))}
                  </Select>
                  <div className="error mt">
                    {errors.budget && t(`budget-${errors.budget.type}`)}
                  </div>
                </FormControl>
              )}
            />
            <footer>
              <Button
                color={"inherit"}
                onClick={handleBack}
                variant={"contained"}
              >
                {t("back")}
              </Button>
              <Button
                disabled={getValues("contactChoice").length === 0}
                onClick={handleNext}
                variant={"contained"}
              >
                {t("next")}
              </Button>
            </footer>
          </StepContent>
        </Step>
        <Step>
          <StepLabel
            onClick={() =>
              getValues("contactChoice").length !== 0 && setActiveStep(4)
            }
          >
            {t("summary")}
          </StepLabel>
          <StepContent className="mt">
            <header>
              <h3>{t("please-review-case-details")}</h3>
            </header>
            <main>{summary}</main>
            <section className="mt mb">
              <Checkbox
                id="kvkk-text"
                value={kvkkChecked}
                checked={kvkkChecked}
                onChange={() => setKvkkChecked((prevState) => !prevState)}
              />
              <label
                htmlFor="kvkk-text"
                dangerouslySetInnerHTML={{ __html: t("kvkk") }}
              />
            </section>
            <footer>
              <Button
                onClick={handleBack}
                disabled={hasSubmit}
                variant={"contained"}
                color={"inherit"}
              >
                {hasSubmit ? <CircularProgress size={"16px"} /> : t("back")}
              </Button>
              <Button
                type="submit"
                disabled={hasSubmit || !kvkkChecked}
                variant={"contained"}
              >
                {hasSubmit ? (
                  <CircularProgress size={"16px"} />
                ) : (
                  t(props.reviewMode ? "save" : "submit")
                )}
              </Button>
            </footer>
          </StepContent>
        </Step>
      </Stepper>
      {props.reviewMode && (
        <>
          {summary}
          <Controller
            name={"description"}
            control={control}
            rules={{ ...caseValidations.description }}
            render={({ field: { onChange, value } }) => (
              <>
                <InputLabel className="mt">
                  <h3>{t("change-short-desc")}</h3>
                </InputLabel>
                <TextField
                  fullWidth
                  error={!!errors.description}
                  placeholder={t("please-write-here")}
                  multiline={true}
                  rows={5}
                  onChange={(e) => {
                    onChange(e);
                    trigger("description").then();
                  }}
                  value={value}
                />
                <div className="error">
                  {errors.description &&
                    t(`short-desc-${errors.description.type}`)}
                </div>
              </>
            )}
          />
          <footer className={"mt"}>
            <Button type="submit" disabled={hasSubmit} variant={"contained"}>
              {hasSubmit ? <CircularProgress size={"16px"} /> : t("save")}
            </Button>
          </footer>
        </>
      )}
    </form>
  );

  const offer = offerStore.lawyerOffers.find((offer) => offer.caseId === id);
  const offerActions = (
    <div className="offerActions">
      {caseItem && (
        <OfferDialog
          caseItem={caseItem}
          offerId={offer?.id}
          open={openOfferDialog}
          setOpen={setOpenOfferDialog}
        />
      )}
      {offerStore.lawyerOffers.length < configStore.appConfig.offerLimit ? (
        <Button
          onClick={handleOfferDialogOpen}
          variant={"contained"}
          size={"large"}
          color={"warning"}
        >
          {t("give-offer")} <i className="fal fa-comment-dollar"></i>
        </Button>
      ) : (
        (configStore.getFlag(Flags.isPremiumEnabled) ?? (
          <Button color={"warning"} size={"large"} variant={"outlined"}>
            {t("more-offers-with-premium", [
              `${offerStore.lawyerOffers.length}`,
              `${configStore.appConfig.offerLimit}`,
            ])}
          </Button>
        ))
      )}
    </div>
  );

  const content = editMode ? (
    <>
      {!isAdmin && !props.reviewMode && offers} {header} {form}
    </>
  ) : (
    <>
      {summary} {offerActions}
    </>
  );

  if (id === undefined && props.caseItem === undefined) {
    return (
      <div className="delegationContainer">{t("missing-case-id-in-url")}</div>
    );
  } else {
    return (
      <div className={props.reviewMode ? "" : "delegationContainer"}>
        {isPageLoaded ? (
          content
        ) : (
          <div className="loadingContainer">
            <CircularProgress />
          </div>
        )}
      </div>
    );
  }
});

export default Delegation;
